import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
	Avatar,
	Box,
	Chip,
	Stack,
	Switch,
	Typography,
} from "@mui/material";
import React from "react";

import {
	FLEX_WORKER_TYPE_ID,
	FULL_TIME_WORKER_TYPE_ID,
} from "../../../../../../../assets/constants";

interface ForcedOvertimeProps {
	config: any;
	setShiftAllocationRules: any;
	shiftAllocationRules: any;
	id: any;
	forcedOTRules: string[];
	allocationType: string;
}

// Rules saved before worker-type selection existed read as Full Time only
const DEFAULT_MANUAL_WORKER_TYPE_IDS = [FULL_TIME_WORKER_TYPE_ID];

const WORKER_TYPE_OPTIONS = [
	{
		id: FULL_TIME_WORKER_TYPE_ID,
		label: "Full Time Worker",
		shiftKind: "Mandatory Overtime Shift",
	},
	{
		id: FLEX_WORKER_TYPE_ID,
		label: "Flex Worker",
		shiftKind: "Flex Shift",
	},
];

const ForcedOvertime: React.FC<ForcedOvertimeProps> = ({
	config,
	setShiftAllocationRules,
	shiftAllocationRules,
	id,
	forcedOTRules,
	allocationType,
}) => {
	const Title: React.FC<{ children: React.ReactNode }> = ({ children }) => (
		<Typography
			variant='body1'
			sx={{
				fontFamily: "Roboto",
				fontSize: "16px",
				fontWeight: 400,
				lineHeight: "24px",
				letterSpacing: "0.15px",
				textAlign: "left",
			}}
		>
			{children}
		</Typography>
	);

	const isEnabled = !!config?.forceAssignSettings?.isEnabled;
	const savedWorkerTypeIds: number[] =
		config?.forceAssignSettings?.manualWorkerTypeIds ??
		DEFAULT_MANUAL_WORKER_TYPE_IDS;
	// The main switch gates the saved list; when it is off, no type is allowed
	const selectedWorkerTypeIds = isEnabled ? savedWorkerTypeIds : [];
	const isFastest = allocationType === "FASTEST";

	const handleWorkerTypeChange = (workerTypeId: number, checked: boolean) => {
		const nextWorkerTypeIds = (
			checked
				? [...selectedWorkerTypeIds, workerTypeId]
				: selectedWorkerTypeIds.filter((typeId) => typeId !== workerTypeId)
		)
			.filter((typeId, index, list) => list.indexOf(typeId) === index)
			.sort((a, b) => a - b);
		const nextIsEnabled = nextWorkerTypeIds.length > 0;

		// Only the rule being edited changes; every other rule keeps the values
		// it was loaded with so an untouched list is never overwritten
		setShiftAllocationRules(
			shiftAllocationRules.map((rule: any) => {
				if (rule.shiftAllocationTypeId !== id) return rule;
				const forceAssignSettings = { ...rule.forceAssignSettings };
				if (nextIsEnabled !== isEnabled) {
					forceAssignSettings.applicableBeforeDays = nextIsEnabled ? 2 : 0;
				}
				return {
					...rule,
					forceAssignSettings: {
						...forceAssignSettings,
						isEnabled: nextIsEnabled,
						manualWorkerTypeIds: nextWorkerTypeIds,
					},
				};
			})
		);
	};

	return (
		<Box>
			<Box display='flex' flexDirection={"column"}>
				<Typography
					variant='body1'
					sx={{
						fontSize: "16px",
						fontWeight: 700,
						lineHeight: "24px",
						letterSpacing: "0.15px",
					}}
				>
					Settings for Supervisor Assignment
				</Typography>
				<Typography
					sx={{
						fontSize: "16px",
						fontWeight: 400,
						lineHeight: 1.5,
						letterSpacing: "0.15px",
						color: "rgba(0, 0, 0, 0.87)",
						marginTop: "4px",
					}}
				>
					{isFastest
						? "Supervisors can assign Workers directly to open spots on the Shift"
						: "Supervisors can assign Workers directly to open spots remaining after voluntary signups"}
				</Typography>
				<Typography
					sx={{
						fontSize: "14px",
						fontWeight: 400,
						lineHeight: 1.43,
						letterSpacing: "0.17px",
						color: "rgba(0, 0, 0, 0.6)",
						marginTop: "12px",
					}}
				>
					Worker types Supervisors can assign
				</Typography>
				{WORKER_TYPE_OPTIONS.map((option) => (
					<Box key={option.id} display={"flex"} alignItems={"center"}>
						<Switch
							checked={selectedWorkerTypeIds.includes(option.id)}
							onChange={(e) =>
								handleWorkerTypeChange(option.id, e.target.checked)
							}
							inputProps={{ "aria-label": option.label }}
							sx={{ marginLeft: "-12px" }}
						/>
						<Typography
							sx={{
								fontSize: "14px",
								lineHeight: 1.43,
								letterSpacing: "0.17px",
								color: "rgba(0, 0, 0, 0.87)",
							}}
						>
							{option.label}
						</Typography>
						<Typography
							sx={{
								fontSize: "14px",
								lineHeight: 1.43,
								letterSpacing: "0.17px",
								color: "rgba(0, 0, 0, 0.6)",
								marginLeft: "8px",
							}}
						>
							{option.shiftKind}
						</Typography>
					</Box>
				))}
				{selectedWorkerTypeIds.length === 0 && (
					<Box
						display='flex'
						alignItems='center'
						gap={1}
						sx={{
							backgroundColor: "#FDF0E6",
							borderRadius: "4px",
							padding: "6px 12px",
							marginTop: "8px",
							maxWidth: "640px",
						}}
					>
						<InfoOutlinedIcon sx={{ fontSize: "20px", color: "#ED6C02" }} />
						<Typography sx={{ fontSize: "14px", color: "#5F2B01" }}>
							Supervisor Assignment is off for this rule. Turn on at least one
							worker type to let Supervisors assign Workers directly.
						</Typography>
					</Box>
				)}
			</Box>
			<Typography
				sx={{
					fontFamily: "Roboto",
					fontWeight: 400,
					fontSize: "12px",
					lineHeight: "166%",
					letterSpacing: "0.4px",
					mt: 2,
				}}
			>
				Note : For Shifts that accept partial signups, Workers assigned by a
				Supervisor will be assigned to the full Shift to fill the open spots
			</Typography>
			{allocationType !== "SENIORITY" && !isFastest && (
				<Box mt={2}>
					<Title>Automatic Assignment Order (among remaining Workers)</Title>
					<Box>
						<Stack direction='column' alignItems='start'>
							{forcedOTRules.map((rule, index) => (
								<Chip
									avatar={<Avatar>{index + 1}</Avatar>}
									label={rule}
									key={rule}
									sx={{
										"& .MuiChip-label": {
											display: "block",
											whiteSpace: "normal",
										},
										marginTop: "8px",
									}}
								/>
							))}
						</Stack>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default ForcedOvertime;
